import { appEnv } from 'app/core/environment';
import { DoesntEndWith, StartsWith } from 'app/tools/general.types';
import _ from 'lodash';

const ApiPaths = {
  REGISTER: '/rest-auth/registration',
  LOGIN: '/accounts/login',
  EXTERNAL_SIGN_IN: '/accounts/social/auth',
  LOGOUT: '/rest-auth/logout',
  CHANGE_PASSWORD: '/rest-auth/password/change',
  RESET_PASSWORD: '/rest-auth/password/reset',
  CONFIRM_PASSWORD: '/rest-auth/password/reset/confirm',

  IDENTITY: '/identity',
  USER_PROFILE: '/consumer/profile',
  USER_DELETE: '/deleteuser',
  USER_PROFILE_CREATE: '/consumer/profile/create',
  USER_PROFILE_UPDATE: '/consumer/profile/update',
  USER_PROFILE_IMG_SET: '/consumer/profile/image',
  USER_ADD_CREDIT_CARD: '/consumer/consumerpaymentmethods',
  USER_ADD_ADDRESSES: '/consumer/delivery-location',
  USER_CARD_INFO: '/consumer/consumerpaymentmethods',
  USER_CARD_DELETE: '/consumer/consumerpaymentmethods',
  USER_HISTORY: '/consumer/consumerpaymenthistory',
  REPAIRER_INFO: '/repairs/repaircompanyinformation',
  DELETE_CHAT: '/repairs/deleterepaircasechat',
  REPAIRERS_ACTIVITY: '/company/repairers/activity',

  VALIDATE_COMPANY_DETAILS_PT1: '/company/repairers/partial/validate',
  CREATE_BUSINESS: '/company/repairers/create',
  CURRENT_REPAIR_CAPABILITIES:
    '/company/repairers/current/capabilities',
  REPAIRER_PERSONAL_ADDRESS:
    '/company/repairers/repairerpersonaladdress',
  SET_REPAIR_CAPABILITIES:
    '/company/repairers/current/capabilities/set',
  REPAIR_LOCATIONS: '/company/repair-locations',
  REPAIR_PERSONAL_DETAIL: '/company/repairers/personaldetails',
  REPAIR_ACCOUNT_TYPE: '/company/repairers/companyacctype',
  CURRENT_COMPANY: '/company/current',
  PUSH_NOTIFICATION: '/pushnotification',
  CURRENT_COMPANY_BANK_DETAILS:
    '/company/repairers/companybankdetails',
  REPAIR_STATS: '/company/repairers/reporting',
  ASSIGNMENT_OPEN_REPAIR: '/consumer/active/repaircase',
  REPAIRER_HISTORY: '/company/repairers/paymenthistory',
  REPAIRCASE_PAYMENT_DETAIL:
    '/company/repairers/repaircasepaymentdetail',

  PRODUCTS: '/product/product-variants/v1',
  PRODUCTSV2: '/product/product-variants/v2',
  PRODUCT_SEARCH: '/product/get-products-for-search',
  MOTORCYCLES: '/product/motorcycle-variants/v1',
  CREATE_MANUAL_PRODUCT: '/product/custom-product-variant',
  PRODUCT_CATEGORIES: '/product/product-categories',
  PRODUCT_BRANDS: '/product/brands',
  PRODUCT_CATEGORIESBYBRAND: '/product/get-category-via-brand',

  ASSIGNED_PRODUCTS: '/consumer/assigned-products',
  REPAIR_ASSIGNED_PRODUCTS:
    '/company/repairers/homescreenproductlist',
  // NOTE: v1 is the new assign product endpoint for the IceCat integration
  ASSIGNED_PRODUCTS_V1: '/consumer/assigned-products/v1',
  PRODUCT_DEFECTS: '/consumer/defects',
  REPAIRERS: '/company/repairers/proximity-search',
  REPAIR_CASES: '/repairs/repair-cases',
  WARRANTY_CLAIM: '/company/repairers/warrantyclaim',
  WARRANTY_CLAIM_PAYMENT: '/consumer/confirmpaymentwithwarranty',
  REPAIR_EVENTS: '/repairs/repair-events',
  REPAIR_IMAGE_EVENT: '/repairs/repair-event/image',
  REFERRAL_CREATE: '/consumer/referrals/create',
  REFERRAL_DETAILS: '/consumer/referraldetails',
  COMPETITIONS_TERMS: '/consumer/competitiontnc',
  QUOTE_SUMMERY: '/repairs/quote-summary',
  QUOTE_SUMMERY_UPDATE: '/repairs/quote-summary-update',
  QUOTE_SUMMERY_PAYMENT: '/consumer/makepayment',
  REPAIR_UPDATE_STATUS: '/repairs/repaircasestatus',
  CALENDAR_LIST: '/company/repairers/calender',
  NOTIFICATION_LIST: '/company/repairers/notificationlist',
  NOTIFICATION_COUNT: '/company/repairers/notificationcount',
  REPAIRER_ACCOUNT_STATUS: '/company/repairers/accountstatus',

  CONSUMER_NOTIFICATION_COUNT: '/consumer/notificationcount',
  REPAIRS_RATING: '/repairs/rating',
  REPAIRS_CASE_WARRANTY_COMMENTS:
    '/repairs/repaircasewarrantyandcomments',
} as const;

const getApiUrls = <
  P extends {
    [K in keyof P]: string &
      DoesntEndWith<StartsWith<P[K], '/'>, '/'>;
  }
>(
  paths: P,
  root: string
): Record<keyof P, string> => _.mapValues(paths, path => root + path);

// NOTE: an error in ApiPaths in the function below is probably caused
// by not starting with a slash, or ending with a slash, in one of the
// path strings.
// NOTE: api root falls back to '' for use in testing
export const ApiUrl = getApiUrls(ApiPaths, appEnv.API_ROOT || '');

type Id = string | number;
const ApiIdUrlPaths = {
  USER_PRODUCT_IMAGE: (id: Id) =>
    `${ApiUrl.ASSIGNED_PRODUCTS}/${id}/user-product-image`,
  ASSIGNED_PRODUCT: (id: Id) => `${ApiUrl.ASSIGNED_PRODUCTS}/${id}`,
};

type ApiIdUrlType = Record<
  keyof typeof ApiIdUrlPaths,
  (id: string | number) => string
>;
// NOTE: this function exists only to enforce the type-checking of
// ApiIdUrlPaths to make sure it follows the correct format
export const ApiIdUrl: ApiIdUrlType = _.identity(ApiIdUrlPaths);

const mockJsonDbRoot = 'http://localhost:3100';
export const mockWithJsonDb = (url: string): string =>
  url
    // remove server api root
    .replace(appEnv.API_ROOT, '')
    // split path into chunks split at "/"s
    .split('/')
    // ignore any empty chunks (from any "/"" at start or end)
    .filter(t => !!t)
    // map over each chunk of the path
    .map((t, i, a) =>
      i === 0
        ? // if it is the first chunk, preface with JSON db root
          `${mockJsonDbRoot}/${t}`
        : i === a.length - 1 && t.match(/^\d$/)
        ? // if last chunk is a digit, add the preceding "/" back in.
          // this is the access method to get a specific array item
          `/${t}`
        : // otherwise join the chunks together with "~", to give the
          // nested path translation (JSON db doesn't handle nested
          // paths access)
          `~${t}`
    )
    .join('');

export const FETCHIFY_SCRIPT_PATH =
  'https://cc-cdn.com/generic/scripts/v1/cc_c2a.min.js';

export enum UrlParamKey {
  ID = 'id',
  PAGE_NUM = 'pageNum',
}

export type UrlParams = Partial<Record<`${UrlParamKey}`, string>>;

type Libraries = (
  | 'drawing'
  | 'geometry'
  | 'localContext'
  | 'places'
  | 'visualization'
)[];

export const googleLibraries: Libraries = [
  'geometry',
  'drawing',
  'places',
];

export enum UrlQueryKey {
  PRODUCT_VARIANT_ID = 'product-variant-id',
  PRODUCT_UPC_ID = 'product-upc-id',
  IS_NEW_DATABASE_PRODUCT = 'is-new-database-product',
  PRODUCT_VARIANT_NAME = 'product-variant-name',
  PRODUCT_ASSIGNMENT_ID = 'product-assignment-id',
  FILTER_CATEGORY = 'filter-category',

  SEARCH = 'search',

  UID = 'uidb64',
  TOKEN = 'token',
  REF_CODE = 'code',
}
